import { useState, useEffect } from 'react';
import { X, Type, ZoomIn, ZoomOut, Contrast, MousePointer2, Link2, Eye, RotateCcw } from 'lucide-react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    contrast: 'normal',
    highlightLinks: false,
    bigCursor: false,
    readableFont: false,
    highlightFocus: false,
  });

  useEffect(() => {
    // Load saved settings
    const saved = localStorage.getItem('accessibilitySettings');
    if (saved) {
      const parsed = JSON.parse(saved);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  const applySettings = (s) => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${s.fontSize}%`;
    
    // Contrast
    root.classList.remove('high-contrast', 'inverted-colors');
    if (s.contrast === 'high') {
      root.classList.add('high-contrast');
    } else if (s.contrast === 'inverted') {
      root.classList.add('inverted-colors');
    }
    
    // Highlight links
    root.classList.toggle('highlight-links', s.highlightLinks);
    
    // Big cursor
    root.classList.toggle('big-cursor', s.bigCursor);
    
    // Readable font
    root.classList.toggle('readable-font', s.readableFont);
    
    // Highlight focus
    root.classList.toggle('highlight-focus', s.highlightFocus);
  };

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(newSettings));
    applySettings(newSettings);
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      contrast: 'normal',
      highlightLinks: false,
      bigCursor: false,
      readableFont: false,
      highlightFocus: false,
    };
    setSettings(defaultSettings);
    localStorage.setItem('accessibilitySettings', JSON.stringify(defaultSettings));
    applySettings(defaultSettings);
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSetting('fontSize', settings.fontSize + 10);
    }
  };

  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSetting('fontSize', settings.fontSize - 10);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 md:bottom-4 right-4 md:right-auto md:left-4 z-40 w-12 h-12 md:w-14 md:h-14 bg-black text-white rounded-full shadow-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
        aria-label="Open accessibility options"
        data-testid="accessibility-widget-btn"
      >
        <svg 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          className="w-7 h-7"
          aria-hidden="true"
        >
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
        </svg>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50"
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="absolute bottom-36 md:bottom-20 right-4 md:right-auto md:left-4 bg-white rounded-xl shadow-2xl w-80 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Accessibility Options"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
                    <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
                  </svg>
                </div>
                <h2 className="font-heading text-base font-bold text-gray-900">Accessibility</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close accessibility options"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Font Size */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
                  <Type className="w-4 h-4" />
                  Text Size
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={decreaseFontSize}
                    disabled={settings.fontSize <= 80}
                    className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    aria-label="Decrease text size"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <div className="flex-1 text-center">
                    <span className="text-lg font-bold text-gray-900">{settings.fontSize}%</span>
                  </div>
                  <button
                    onClick={increaseFontSize}
                    disabled={settings.fontSize >= 150}
                    className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    aria-label="Increase text size"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Contrast */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm">
                  <Contrast className="w-4 h-4" />
                  Contrast
                </h3>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { value: 'normal', label: 'Normal' },
                    { value: 'high', label: 'High' },
                    { value: 'inverted', label: 'Invert' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateSetting('contrast', option.value)}
                      className={`p-2 rounded-lg border text-xs transition-colors ${
                        settings.contrast === option.value
                          ? 'border-black bg-black text-white'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-2">
                <ToggleOption
                  icon={<Link2 className="w-4 h-4" />}
                  label="Highlight Links"
                  checked={settings.highlightLinks}
                  onChange={(v) => updateSetting('highlightLinks', v)}
                />
                <ToggleOption
                  icon={<MousePointer2 className="w-4 h-4" />}
                  label="Big Cursor"
                  checked={settings.bigCursor}
                  onChange={(v) => updateSetting('bigCursor', v)}
                />
                <ToggleOption
                  icon={<Type className="w-4 h-4" />}
                  label="Readable Font"
                  checked={settings.readableFont}
                  onChange={(v) => updateSetting('readableFont', v)}
                />
                <ToggleOption
                  icon={<Eye className="w-4 h-4" />}
                  label="Highlight Focus"
                  checked={settings.highlightFocus}
                  onChange={(v) => updateSetting('highlightFocus', v)}
                />
              </div>

              {/* Reset Button */}
              <button
                onClick={resetSettings}
                className="w-full p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              >
                <RotateCcw className="w-4 h-4" />
                Reset All
              </button>

              {/* Accessibility Statement Link */}
              <a
                href="/accessibility"
                className="block text-center text-xs text-gray-600 hover:text-black underline"
              >
                Accessibility Statement
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ToggleOption = ({ icon, label, checked, onChange }) => (
  <button
    onClick={() => onChange(!checked)}
    className={`w-full p-3 rounded-lg border transition-colors flex items-center gap-2 text-sm ${
      checked
        ? 'border-black bg-black text-white'
        : 'border-gray-200 hover:border-gray-300'
    }`}
    role="switch"
    aria-checked={checked}
  >
    {icon}
    <span className="font-medium flex-1 text-left">{label}</span>
    <div className={`w-8 h-5 rounded-full transition-colors ${checked ? 'bg-white' : 'bg-gray-300'}`}>
      <div className={`w-4 h-4 rounded-full transition-transform mt-0.5 ${checked ? 'bg-black translate-x-3.5 ml-0.5' : 'bg-white ml-0.5'}`} />
    </div>
  </button>
);

export default AccessibilityWidget;
